import { motion } from "motion/react";
import type React from "react";
import { useMemo, useRef, useState } from "react";

import { Spread } from "@/design-system/Spread";
import { useResizeObserver } from "@/hooks/useResizeObserver";
import { RuntimeChartSegment, RuntimeChartTranslate } from "@/ui-adapters/RuntimeChartGeometry";
import { RuntimeWidth } from "@/ui-adapters/RuntimeWidth";

const WIDTH_INDICATOR = 4;

interface BenchmarkMeterChartProps {
  /** The primary value to display on the chart (e.g., 25 for 25%). */
  value: number;
  /** The benchmark value to compare against (e.g., 50 for 50%). */
  benchmark: number;
  /** Determines if the chart should be in its animated/final state. */
  isDataAvailable: boolean;
  /** ARIA label for the chart container for accessibility. */
  ariaLabel: string;
}

export function BenchmarkMeterChart({
  value,
  benchmark,
  isDataAvailable,
  ariaLabel,
}: BenchmarkMeterChartProps): React.ReactNode {
  const refChartContainer = useRef<HTMLDivElement>(null);
  const [chartWidth, setChartWidth] = useState(0);
  const refIndicatorValue = useRef<HTMLHeadingElement>(null);
  const [indicatorValueWidth, setIndicatorValueWidth] = useState(0);

  useResizeObserver(refIndicatorValue, (entries) => {
    for (const entry of entries) {
      if (entry.contentRect) {
        setIndicatorValueWidth(entry.contentRect.width);
      }
    }
  });

  useResizeObserver(refChartContainer, (entries) => {
    for (const entry of entries) {
      if (entry.contentRect) {
        setChartWidth(entry.contentRect.width);
      }
    }
  });

  const bars = useMemo(() => {
    // Position of the benchmark is always 70% of the chart width
    const posBenchmark = 0.7 * chartWidth;
    // Amount of the chart width that is on either side of the benchmark
    const leftWidth = posBenchmark;
    const rightWidth = chartWidth - leftWidth;
    // Width per percentage on either side side of the benchmark
    const stepLeft = leftWidth / (benchmark || 0.001); // Avoid division by zero
    const stepRight = rightWidth / (100 - benchmark);
    // Start and end positions of the colored bars
    const posStartRed = 0;
    const posStartOrange = 0.1 * benchmark * stepLeft; // 10% of benchmark
    const posStartYellow = 0.3 * benchmark * stepLeft; // 30% of benchmark
    const posStartGreen = 0.9 * benchmark * stepLeft; // 90% of benchmark
    const posEndRed = posStartOrange;
    const posEndOrange = posStartYellow;
    const posEndYellow = posStartGreen;
    const posEndGreen = chartWidth;

    // Position of the primary value depending on which side of the benchmark it falls on
    let posPrimaryDataValue = value <= benchmark ? value * stepLeft : (value - benchmark) * stepRight + posBenchmark;
    if (posPrimaryDataValue + WIDTH_INDICATOR > chartWidth) {
      // adjust for label
      posPrimaryDataValue = chartWidth - WIDTH_INDICATOR;
    }

    return {
      posStartRed,
      posStartOrange,
      posStartYellow,
      posStartGreen,
      posEndRed,
      posEndOrange,
      posEndYellow,
      posEndGreen,
      posPrimaryDataValue,
    };
  }, [chartWidth, value, benchmark]);

  // Calculate indicator position
  const availableChartWidth = chartWidth - WIDTH_INDICATOR;
  // Calculate left and right compensation for the indicator to prevent overflow at extremes
  const isLeftSideEnough = bars.posPrimaryDataValue >= indicatorValueWidth / 2;
  const isRightSideEnough = bars.posPrimaryDataValue + indicatorValueWidth / 2 <= availableChartWidth;
  const leftSideCompensationWidth = isLeftSideEnough ? 0 : indicatorValueWidth / 2 - bars.posPrimaryDataValue;
  const rightSideCompensationWidth = isRightSideEnough
    ? 0
    : bars.posPrimaryDataValue + indicatorValueWidth / 2 - availableChartWidth;

  return (
    <figure className="my-space-sm flex h-meter-frame w-full items-end" aria-label={ariaLabel}>
      <section className="flex w-full flex-col gap-space-xs">
        <div ref={refChartContainer} className="relative w-full">
          {/* Bars */}
          <ul className="relative flex w-full [&>li]:absolute [&>li]:bottom-0 [&>li]:h-meter-bar">
            <RuntimeChartSegment start={bars.posStartRed} end={bars.posEndRed}>
              {(attributes) => <li {...attributes} className="bg-background-danger" />}
            </RuntimeChartSegment>
            <RuntimeChartSegment start={bars.posStartOrange} end={bars.posEndOrange}>
              {(attributes) => <li {...attributes} className="bg-background-warning" />}
            </RuntimeChartSegment>
            <RuntimeChartSegment start={bars.posStartYellow} end={bars.posEndYellow}>
              {(attributes) => <li {...attributes} className="bg-chart-threshold-caution" />}
            </RuntimeChartSegment>
            <RuntimeChartSegment start={bars.posStartGreen} end={bars.posEndGreen}>
              {(attributes) => <li {...attributes} className="bg-background-success" />}
            </RuntimeChartSegment>
            {/* Mask to block the unfilled part of the chart */}
            <motion.span
              variants={{
                initial: {
                  width: chartWidth,
                },
                animate: {
                  width: chartWidth - bars.posPrimaryDataValue + 1,
                },
              }}
              initial="initial"
              animate={isDataAvailable ? "animate" : "initial"}
              transition={{ duration: 0.8, ease: [0.4, 0, 0, 0.99], delay: 0.2 }}
              className="absolute -right-px bottom-0.5 z-10 h-meter-bar bg-background-default"
            >
              {/* Project value indicator */}
              <RuntimeWidth width={WIDTH_INDICATOR}>
                {(indicatorAttributes) => (
                  <div
                    {...indicatorAttributes}
                    className="absolute -bottom-0.5 z-20 flex flex-col items-center gap-space-xs"
                  >
                    <RuntimeChartTranslate offset={leftSideCompensationWidth - rightSideCompensationWidth}>
                      {(labelAttributes) => (
                        <motion.h4
                          {...labelAttributes}
                          ref={refIndicatorValue}
                          variants={{
                            initial: { opacity: 0 },
                            animate: { opacity: 1 },
                          }}
                          initial="initial"
                          animate={isDataAvailable ? "animate" : "initial"}
                          transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
                          className="text-heading-1 text-text-brand-secondary"
                        >
                          {value}%
                        </motion.h4>
                      )}
                    </RuntimeChartTranslate>
                    <RuntimeWidth width={WIDTH_INDICATOR}>
                      {(lineAttributes) => (
                        <span {...lineAttributes} className="h-meter-indicator bg-background-brand-secondary" />
                      )}
                    </RuntimeWidth>
                  </div>
                )}
              </RuntimeWidth>
            </motion.span>
          </ul>
        </div>
        {/* Labels */}
        <Spread>
          <span aria-label="0%" className="text-overline text-text-secondary">
            0
          </span>
          <span aria-label="100%" className="text-overline text-text-secondary">
            100
          </span>
        </Spread>
      </section>
    </figure>
  );
}
